/**
 * Status Action Options
 *
 * Configuration for status change action menu items.
 * Extracted from index.tsx row actions to centralize status change definitions.
 */

import { XCircleIcon } from 'lucide-react';
import React from 'react';

import { getBaseRiskStatusOptions } from '@/components/ui/RiskDropdown';
import { RiskStatus } from '@/types';
import {
  getStatusLabel,
  type VulnerabilityStatusMode,
} from '@/utils/vulnerabilityStatus.util';

interface StatusOption {
  label: string;
  type?: 'label';
  icon?: React.JSX.Element;
  value?: string;
  isDelete?: boolean;
}

/**
 * Get status change menu options for a vulnerability
 * Combines base status options with the Delete/Reject option
 *
 * @param isPraetorianUser - Whether user is a Praetorian user (default: false)
 * @param mode - The current vulnerability status mode (default: 'legacy')
 * @returns Array of status change options filtered by user type
 *
 * @example
 * ```typescript
 * const { isPraetorianUser } = useAuth();
 * const { mode } = useVulnerabilityStatusMode();
 * const options = getStatusChangeOptions(isPraetorianUser, mode);
 * ```
 */
export function getStatusChangeOptions(
  isPraetorianUser = false,
  mode: VulnerabilityStatusMode = 'legacy'
): StatusOption[] {
  // Get status options filtered by user type with mode-aware labels
  const availableStatusOptions = getBaseRiskStatusOptions(
    isPraetorianUser,
    mode
  );

  return [
    {
      label: 'Change Status',
      type: 'label',
    },
    ...availableStatusOptions.map(option => ({
      label: option.label,
      icon: option.icon,
      value: option.value,
    })),
    {
      label: getStatusLabel(RiskStatus.Deleted, mode),
      icon: <XCircleIcon className="size-5" />,
      value: RiskStatus.Deleted,
      isDelete: true, // Flag to trigger delete modal
    },
  ];
}
